import { AssignmentTurnedIn, Warning } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Stack,
  Tab,
  Tabs
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { ReactNode, SyntheticEvent, useState, useMemo } from "react";
import { MedicalNotes } from "./components/MedicalNotes";
import { NoteReport } from "./components/NoteReport";
import { Solicitations } from "./components/Solicitations";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import dayjs from "dayjs";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ maxHeight: "60vh", pt: 2, overflow: "auto" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function Documents({
  medicalNoteData,
  solicitationData,
  resultsData,
  NoteReportData,
  setRefetch,
  title,
  handleOpen,
  setBinaryUrl,
  sign,
}: any) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { userPractitionerRole, userPractitioner } = usePractitionerRoleContext();

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Filtra os documentos de acordo com o status de assinatura
  const medicalNote = medicalNoteData?.filter((e: any) => {
    return sign
      ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
      : e?.resource?.resourceType === "DocumentReference" &&
          e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin";
  });

  const solicitation = solicitationData?.filter((e: any) => {
    return sign
      ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code ===
          "ProofOfOrigin" &&
          e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação"
      : e?.resource?.resourceType === "DocumentReference" &&
          e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin" &&
          e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação";
  });

  const noteReport = NoteReportData?.filter((e: any) => {
    return sign
      ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
      : e?.resource?.resourceType === "DocumentReference" &&
          e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin";
  });

  // Processa e filtra os dados de medicalNote completamente
  // Prepara os dados já processados para serem passados ao MedicalNotes
  const processedMedicalNoteData = useMemo(() => {
    // Filtra DocumentReference conforme o status de assinatura
    const documentReference = medicalNoteData
      ?.filter((el: any) => el?.resource?.resourceType === "DocumentReference")
      ?.filter((e: any) => {
        return sign
          ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
          : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !== "ProofOfOrigin";
      });

    // Filtra MedicationRequest conforme o status de assinatura e practitioner
    const medicationRequest = medicalNoteData?.filter(
      (el: any) =>
        el?.resource?.resourceType === "MedicationRequest" &&
        (sign
          ? true
          : el?.resource?.requester?.reference?.split("/")[1] ===
            userPractitionerRole?.id)
    );

    // Processa cada DocumentReference completamente, criando o rowData
    const rowData =
      documentReference?.map(
        (el: any, index: number) => {
          // Encontra o practitioner no contained
          const auxPractitioner = el?.resource?.contained?.find(
            (contained: any) => contained?.resourceType === "Practitioner"
          );

          // Encontra a especialidade
          const auxSpecialty =
            el?.resource?.practiceSetting?.coding?.[0]?.display;

          // Encontra os MedicationRequest relacionados através do basedOn
          const auxDescription = medicationRequest?.filter((request: any) => {
            const findRef = el?.resource?.basedOn?.find((basedOn: any) =>
              basedOn?.reference.includes(request.resource.id)
            );
            return findRef;
          });

          // Tipo do documento
          const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

          return {
            id: index,
            sign,
            type: auxType,
            fullResource: el?.resource,
            fullMedicationResource: auxDescription,
            description:
              auxDescription
                ?.map(
                  (medicationRequestPrescription: any) =>
                    medicationRequestPrescription?.resource?.medication?.concept
                      ?.text
                )
                ?.join(" , ") || "",
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value,
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
            medication: auxDescription,
          };
        }
      )
        // Aplica o filtro externo: apenas documentos com fullMedicationResource.length > 0
        ?.filter(
          (document: any) => document?.fullMedicationResource?.length > 0
        ) || [];

    return rowData;
  }, [medicalNoteData, sign, userPractitionerRole?.id]);

  // Conta os documentos processados e filtrados
  const filteredMedicalNoteCount = processedMedicalNoteData?.length || 0;

  // Função auxiliar para limpar e juntar expressões (usada em Solicitations)
  function cleanAndJoin(expressions: any, delimiter = ", ") {
    return Array.from(
      new Set(expressions.filter((item: any) => item && item.trim() !== ""))
    ).join(delimiter);
  }

  // Função auxiliar para traduzir categoria (usada em Solicitations)
  function translateCategory(category: any) {
    if (category === "Laboratory procedure") {
      return "Exame Laboratorial";
    } else if (category === "Imaging") {
      return "Exame de imagem";
    } else {
      return category;
    }
  }

  // Função auxiliar para comparar practitioner IDs
  // Compara o ID do practitioner do documento com o userPractitioner
  function matchesPractitioner(documentPractitionerId: string | undefined): boolean {
    if (!documentPractitionerId || !userPractitioner?.id) return false;
    
    // Extrai apenas o CPF/username de ambos os IDs (remove o prefixo oid)
    const docId = documentPractitionerId.includes("-") 
      ? documentPractitionerId.split("-")[1] 
      : documentPractitionerId;
    const userId = userPractitioner.id.includes("-")
      ? userPractitioner.id.split("-")[1]
      : userPractitioner.id;
    
    // Compara os IDs ou verifica se o ID do documento contém o ID do usuário
    return docId === userId || documentPractitionerId === userPractitioner.id;
  }

  // Processa e filtra os dados de solicitation completamente
  // Prepara os dados já processados para serem passados ao Solicitations
  const processedSolicitationData = useMemo(() => {
    // Filtra DocumentReference conforme o status de assinatura e categoria
    const documentReference = solicitationData
      ?.filter((el: any) => el?.resource?.resourceType === "DocumentReference")
      ?.filter((e: any) => {
        return sign
          ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin" &&
              e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação"
          : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !== "ProofOfOrigin" &&
              e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação";
      });

    // Filtra ServiceRequest
    const serviceRequest = solicitationData?.filter(
      (el: any) => el?.resource?.resourceType === "ServiceRequest"
    );

    // Processa cada DocumentReference completamente, criando o rowData
    const rowData =
      documentReference?.map((el: any, index: number) => {
        // Encontra o practitioner no contained
        const auxPractitioner = el?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner"
        );

        // Encontra a especialidade
        const auxSpecialty =
          el?.resource?.practiceSetting?.coding?.[0]?.display;

        // Encontra os ServiceRequest relacionados através do basedOn
        const auxDescription = serviceRequest?.filter((request: any) => {
          const findRef = el?.resource?.basedOn?.find((basedOn: any) =>
            basedOn?.reference.includes(request.resource.id)
          );
          return findRef;
        });

        // Processa categoria
        const auxCategory = cleanAndJoin(
          auxDescription
            ?.filter(
              (item: any) => item !== null && item !== undefined && item !== ""
            )
            ?.map((el: any) => el?.resource?.category?.[0]?.coding?.[0].display)
        );

        // Processa resultados
        const auxResults = resultsData?.[0]?.entry?.filter((result: any) => {
          const findRef = auxDescription?.find(
            (service: any) =>
              service?.resource?.id ===
              result?.resource?.basedOn
                ?.find((bas: any) => bas.reference.includes("ServiceRequest"))
                ?.reference?.split("/")?.[1]
          );
          return findRef;
        });

        const resultUrls =
          auxResults
            ?.map(
              (result: any) =>
                result?.resource?.content?.[0]?.attachment?.url || ""
            )
            .filter((url: string) => url !== "") || [];

        const resultUrl = resultUrls.length > 0 ? resultUrls[0] : "";

        // Tipo do documento
        const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

        // Identificador do practitioner para filtro
        const practitionerId =
          auxPractitioner?.identifier?.[0]?.value;

        return {
          id: index,
          sign,
          type: auxType,
          fullResource: el?.resource,
          fullServiceResource: auxDescription,
          category: translateCategory(auxCategory),
          description:
            `${auxDescription
              ?.map(
                (serviceRequest: any) =>
                  serviceRequest?.resource?.code?.concept?.coding?.[0]?.display
              )
              ?.join(", ")}` || "",
          date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
          specialty: auxSpecialty || "-",
          practitioner:
            auxPractitioner?.name?.[0]?.given?.[0] ||
            auxPractitioner?.identifier?.[0]?.value,
          practitionerId: practitionerId,
          binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
          auxResults,
          resultUrls,
          resultUrl,
        };
      })
        // Filtra por userPractitioner: apenas documentos do practitioner logado quando não assinado
        ?.filter((document: any) => {
          if (sign) return true; // Se for assinado, mostra todos
          // Aplica o filtro apenas quando não assinado
          return matchesPractitioner(document?.practitionerId);
        }) || [];

    return rowData;
  }, [solicitationData, resultsData, sign, userPractitioner?.id]);

  // Conta os documentos processados e filtrados de solicitation
  const filteredSolicitationCount = processedSolicitationData?.length || 0;

  // Processa e filtra os dados de NoteReport completamente
  // Prepara os dados já processados para serem passados ao NoteReport
  const processedNoteReportData = useMemo(() => {
    // Processa cada DocumentReference completamente, criando o rowData
    const rowData =
      NoteReportData?.filter((e: any) => {
        return sign
          ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
          : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !== "ProofOfOrigin";
      })
        ?.map((el: any, index: number) => {
          // Encontra o practitioner no contained
          const auxPractitioner = el?.resource?.contained?.find(
            (contained: any) => contained?.resourceType === "Practitioner"
          );

          // Encontra a especialidade
          const auxSpecialty =
            el?.resource?.practiceSetting?.coding?.[0]?.display;

          // Identificador do practitioner para filtro
          const practitionerId =
            auxPractitioner?.identifier?.[0]?.value;

          return {
            id: index,
            fullResource: el?.resource,
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value,
            practitionerId: practitionerId,
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            sign,
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
          };
        })
        // Filtra por userPractitioner: apenas documentos do practitioner logado quando não assinado
        ?.filter((document: any) => {
          if (sign) return true; // Se for assinado, mostra todos
          // Aplica o filtro apenas quando não assinado
          return matchesPractitioner(document?.practitionerId);
        }) || [];

    return rowData;
  }, [NoteReportData, sign, userPractitioner?.id]);

  // Conta os documentos processados e filtrados de NoteReport
  const filteredNoteReportCount = processedNoteReportData?.length || 0;

  return (
    <>
      <Accordion sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {sign ? (
              <>
                <AssignmentTurnedIn sx={{ color: "success600.main" }} />
                Documentos assinados
              </>
            ) : (
              <>
                <Warning sx={{ color: "#FBBB3C" }} />
                Assinaturas pendentes
              </>
            )}
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {filteredMedicalNoteCount +
                filteredSolicitationCount +
                filteredNoteReportCount || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              aria-label="basic tabs example"
              variant="scrollable"
            >
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Receitas médicas
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredMedicalNoteCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(0)}
              />
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Solicitações de exame
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredSolicitationCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(1)}
              />
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Relatórios e atestados
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredNoteReportCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(2)}
              />
            </Tabs>
          </Box>

          <CustomTabPanel value={selectedTab} index={0}>
            <MedicalNotes
              setRefetch={setRefetch}
              sign={sign}
              rowData={processedMedicalNoteData}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
            />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1}>
            <Solicitations
              sign={sign}
              setRefetch={setRefetch}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
              rowData={processedSolicitationData}
              resultsData={resultsData}
            />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={2}>
            <NoteReport
              setRefetch={setRefetch}
              sign={sign}
              rowData={processedNoteReportData}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
            />
          </CustomTabPanel>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
